/*
 * Colour swatches on a product card.
 *
 * Horizon's own product-form-component reads the variant from a single
 * `input[name="id"]`, so the swatches cannot carry that name themselves — a
 * group of radios would hand it the first one rather than the checked one.
 * They are therefore an ordinary radio group, and this file mirrors the choice
 * into the hidden input, then updates the photo and the two prices.
 *
 * Everything is delegated from the document, so cards that arrive later — a
 * section re-render in the theme editor, a future paginated grid — work without
 * being told about. Nothing here runs unless a swatch is actually clicked.
 *
 * Without this file the swatches are inert and the button adds the default
 * variant. That is the same behaviour as a card with no swatches at all, which
 * is why the markup does not depend on it.
 */

(() => {
  // The section is used twice on the homepage. Keep every declaration scoped
  // so the second classic-script execution cannot redeclare top-level consts.
  if (window.ppProductCardReady) return;
  window.ppProductCardReady = true;

  const CARD = '.pp-product-card';
  const CAROUSEL = '.pp-product-carousel__viewport';
  const SWATCH = 'input[data-pp-swatch]';
  const TITLE = '.pp-product-card__title';

  // Latest crossfade token per primary image, so a superseded swap's late
  // preload cannot flip the photo back. See crossfadeVariantImage.
  const swapTokens = new WeakMap();

/**
 * Applies a swatch selection to the card it belongs to.
 *
 * @param {HTMLInputElement} swatch
 */
  function selectSwatch(swatch) {
    const card = swatch.closest(CARD);
    if (!card) return;

    const variantInput = card.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = swatch.value;
      // Horizon listens for input on the variant field to keep its own state in
      // step, and the event has to bubble to reach the form component.
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const { price, imageSrc, imageSrcset } = swatch.dataset;

    if (price) {
      for (const node of card.querySelectorAll('[data-pp-price]')) {
        node.textContent = price;
      }
    }

    const image = card.querySelector('.pp-product-card__image:not(.pp-product-card__image--hover)');
    if (image instanceof HTMLImageElement && imageSrc) {
      crossfadeVariantImage(image, imageSrc, imageSrcset);
    }

    const button = card.querySelector('.pp-product-card__add');
    if (button instanceof HTMLButtonElement) {
      button.disabled = swatch.dataset.unavailable === 'true';
    }

    // The photo now shows the chosen variant. On a touch screen, stop the
    // in-view hover video from playing over it (defined in the hover section
    // below; a no-op on desktop and on cards without a hover video).
    suppressHoverVideo(card);
  }

  /**
   * Swaps the primary photo to a chosen variant using the card's own crossfade,
   * so a colour change reads the same as the hover reveal instead of snapping: a
   * clone of the outgoing photo is laid over the image and faded out as the new
   * source loads underneath it. Reduced motion swaps outright, with no clone.
   *
   * `src`/`srcset` are not animatable properties, which is why the fade has to
   * ride a separate element rather than the image itself.
   *
   * @param {HTMLImageElement} image
   * @param {string} src
   * @param {string} [srcset]
   */
  function crossfadeVariantImage(image, src, srcset) {
    const apply = () => {
      image.src = src;
      if (srcset) image.srcset = srcset;
    };

    // reducedMotion is declared with the hover code below; it is initialised
    // long before any swatch can be clicked, so this reference is safe.
    if (reducedMotion.matches) {
      apply();
      return;
    }

    // A token per image so a rapid second click supersedes the first: the
    // earlier preload can still resolve late, and without this its stale reveal
    // would flip the photo back to the variant the customer already left.
    const token = (swapTokens.get(image) || 0) + 1;
    swapTokens.set(image, token);

    // Drop any overlay still fading from a rapid previous click.
    image.parentElement?.querySelector('.pp-product-card__image--swap')?.remove();

    const overlay = /** @type {HTMLImageElement} */ (image.cloneNode(false));
    overlay.classList.add('pp-product-card__image--swap');
    overlay.classList.remove('pp-product-card__image');
    overlay.setAttribute('aria-hidden', 'true');
    image.insertAdjacentElement('afterend', overlay);

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      // A newer click has taken over: leave the photo to it, just clear this
      // clone.
      if (swapTokens.get(image) !== token) {
        overlay.remove();
        return;
      }
      // Put the new source on the primary while the clone still covers it, then
      // fade the clone out over the loaded image. Two frames so the swap has
      // painted before the transition starts.
      apply();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          overlay.style.opacity = '0';
        })
      );
    };

    const done = () => overlay.remove();
    overlay.addEventListener('transitionend', done, { once: true });
    // Safety net if transitionend never arrives.
    setTimeout(done, 1500);

    // Fade in once the chosen photo has loaded, so the crossfade lands on a
    // painted image rather than a blank box. Cached images may already be ready.
    const preload = new Image();
    if (srcset) preload.srcset = srcset;
    preload.src = src;
    if (preload.complete) {
      reveal();
    } else {
      preload.addEventListener('load', reveal, { once: true });
      preload.addEventListener('error', reveal, { once: true });
    }
  }

  /**
   * Gives every title in one carousel the height of its tallest natural title.
   * One-line rows therefore stay one line with the designed 4px subtitle gap;
   * the shared row grows only when a sibling actually wraps.
   *
   * @param {Element} carousel
   */
  function syncTitleHeights(carousel) {
    const titles = [...carousel.querySelectorAll(TITLE)];
    if (titles.length === 0) return;

    for (const title of titles) {
      title.style.removeProperty('--pp-card-title-height');
    }

    const tallestTitle = Math.max(...titles.map((title) => Math.ceil(title.getBoundingClientRect().height)));
    if (tallestTitle <= 0) return;

    for (const title of titles) {
      title.style.setProperty('--pp-card-title-height', `${tallestTitle}px`);
    }
  }

  let titleSyncFrame = 0;

  function scheduleTitleSync() {
    cancelAnimationFrame(titleSyncFrame);
    titleSyncFrame = requestAnimationFrame(() => {
      for (const carousel of document.querySelectorAll(CAROUSEL)) {
        syncTitleHeights(carousel);
      }
    });
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches(SWATCH)) return;
    selectSwatch(target);
  });

  window.addEventListener('resize', scheduleTitleSync, { passive: true });
  document.addEventListener('shopify:section:load', scheduleTitleSync);
  document.fonts?.ready.then(scheduleTitleSync);
  scheduleTitleSync();

  /* ------------------------------------------------------------ hover video */

  /*
   * A product can name a video for its hover layer through a metafield. The
   * reveal itself is the stylesheet's existing crossfade, so this only rewinds
   * and plays, which is also what makes leaving the card return to the first
   * image: pausing at zero leaves the poster showing under a hidden layer.
   *
   * Nothing here runs on a card whose hover layer is an image, and no video is
   * fetched until one is asked for: the element carries preload="none" and no
   * autoplay attribute.
   */

  const HOVER_VIDEO = '[data-pp-hover-video]';
  const PLAYING = 'pp-product-card--playing';
  // Set once a variant is chosen. The stylesheet reads it to keep the hover
  // video hidden from then on, which the JS alone cannot do: focusing the
  // chosen swatch triggers :focus-within, and that rule reveals the layer.
  const CHOSEN = 'pp-product-card--variant-chosen';

  // On a touch screen the video plays only after the card has stayed in view
  // this long, rather than the instant it crosses the threshold: a card
  // scrolled quickly past never starts, and one the customer rests on begins on
  // its own. Started at 3 seconds on 2026-08-30, then tuned down to 500ms.
  const IN_VIEW_DELAY = 500;

  // How much of the card must be on screen before its video may play.
  const PLAY_RATIO = 0.6;

  // A card whose variant the customer has actively chosen through a swatch is
  // dropped from the hover video for good: the swatch has already swapped the
  // main photo, so the video — which belongs to a different variant — would
  // only confuse. This holds on desktop hover as well as touch in-view play.
  const variantChosen = new WeakSet();

  // Pending in-view start timers, one per video, so leaving the card before the
  // delay elapses cancels the play rather than letting it fire late.
  const inViewTimers = new WeakMap();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Set once the browser has refused a muted autoplay (see playHoverVideo).
  // From then on, touch screens stop fetching and playing hover videos.
  let autoplayBlocked = false;
  const canHover = window.matchMedia('(hover: hover)');

  /**
   * Rewinds and plays a hover video, ignoring the promise rejection a browser
   * raises when the pointer has already left again.
   *
   * @param {HTMLVideoElement} video
   */
  function playHoverVideo(video) {
    if (reducedMotion.matches) return Promise.resolve(false);
    video.currentTime = 0;
    const attempt = video.play?.();
    if (!attempt) return Promise.resolve(true);
    // Rejected when the pointer has already left again, and on iOS whenever
    // the device blocks autoplay — Low Power Mode does, even for a muted
    // video, and nothing on the page can lift that. Resolves false then, so
    // a caller can leave the photo showing instead of an empty layer.
    return attempt.then(
      () => true,
      (error) => {
        // NotAllowedError on a muted, inline video is the browser refusing
        // autoplay outright — iOS Low Power Mode, or a data saver. There is
        // no API that reports those modes directly; this refusal is the
        // signal. Remembered for the page, so no other card downloads a
        // video that would be refused too.
        if (error?.name === 'NotAllowedError') autoplayBlocked = true;
        return false;
      }
    );
  }

  /**
   * Starts fetching a hover video before it is asked to play, so the play on
   * a touch screen finds frames ready instead of a blank layer on a slow
   * connection. Once per video; the element keeps preload="none" in the
   * markup so cards far down the page cost nothing.
   *
   * @param {HTMLVideoElement} video
   */
  function warmHoverVideo(video) {
    if (autoplayBlocked || video.dataset.ppWarmed) return;
    video.dataset.ppWarmed = 'true';

    // A touch screen whose browser plays HLS itself takes the adaptive
    // stream, so a weak connection gets a lighter rendition. A desktop keeps
    // the direct MP4 in the markup: it starts faster on hover.
    const hls = video.dataset.ppHlsSrc;
    if (hls && !canHover.matches && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hls;
    }

    video.preload = 'auto';
    video.load();
  }

  /*
   * On a desktop the video starts on hover, and fetching it only then is
   * what made the first hover wait. Cards on screen buffer their video
   * ahead instead, so the hover finds frames ready. Only videos near the
   * viewport are fetched, never the whole grid.
   */
  let warmObserver = null;

  function warmDesktopHoverVideos() {
    if (!canHover.matches || reducedMotion.matches) return;
    if (typeof IntersectionObserver !== 'function') return;

    warmObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const video = entry.target;
          if (video instanceof HTMLVideoElement) warmHoverVideo(video);
          warmObserver.unobserve(video);
        }
      },
      { rootMargin: '200px 0px' }
    );

    for (const video of document.querySelectorAll(HOVER_VIDEO)) {
      if (video instanceof HTMLVideoElement && !video.dataset.ppWarmed) warmObserver.observe(video);
    }
  }

  /**
   * Stops a hover video and puts it back at its first frame, so the next hover
   * starts from the beginning rather than resuming.
   *
   * @param {HTMLVideoElement} video
   */
  function resetHoverVideo(video) {
    video.pause?.();
    video.currentTime = 0;
  }

  /**
   * Drops a card from the touch-screen in-view autoplay once the customer has
   * chosen a variant through its swatches: the swatch has already swapped the
   * photo, so a hover video over the other variant would only confuse. Any
   * pending or running play is undone and the video is unobserved so a later
   * scroll cannot restart it. A no-op on desktop, where the observer never runs.
   *
   * @param {Element} card
   */
  function suppressHoverVideo(card) {
    if (!card) return;
    variantChosen.add(card);

    const video = card.querySelector(HOVER_VIDEO);
    if (!(video instanceof HTMLVideoElement)) return;

    // The class hides the layer over every reveal state (hover, focus, in-view);
    // the rest tears down the touch-screen autoplay so it cannot come back.
    card.classList.add(CHOSEN);

    const timer = inViewTimers.get(video);
    if (timer) {
      clearTimeout(timer);
      inViewTimers.delete(video);
    }
    card.classList.remove(PLAYING);
    resetHoverVideo(video);
    inViewObserver?.unobserve(video);
  }

  document.addEventListener(
    'mouseover',
    (event) => {
      if (!canHover.matches) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      const card = target.closest(CARD);
      if (!card || card.contains(event.relatedTarget)) return;

      const video = card.querySelector(HOVER_VIDEO);
      if (video instanceof HTMLVideoElement) playHoverVideo(video);
    },
    { passive: true }
  );

  document.addEventListener(
    'mouseout',
    (event) => {
      if (!canHover.matches) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      const card = target.closest(CARD);
      if (!card || card.contains(event.relatedTarget)) return;

      const video = card.querySelector(HOVER_VIDEO);
      if (video instanceof HTMLVideoElement) resetHoverVideo(video);
    },
    { passive: true }
  );

  /*
   * A touch screen has no hover, so the video plays when the card comes into
   * view instead. The class is what reveals it there, since :hover never fires.
   * Each video carries its own opt in, because the client asked to be able to
   * switch this off.
   */
  let inViewObserver = null;

  function watchHoverVideos() {
    if (canHover.matches || reducedMotion.matches) return;
    if (typeof IntersectionObserver !== 'function') return;

    inViewObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;

          const card = video.closest(CARD);

          // A chosen variant has taken this card out of autoplay for good;
          // stop observing so a lingering intersection cannot reveal it.
          if (card && variantChosen.has(card)) {
            inViewObserver.unobserve(video);
            continue;
          }

          if (entry.isIntersecting) {
            // Begin buffering on the first sliver of the card, well before the
            // play threshold, so the countdown below overlaps the download.
            // Below the threshold nothing else changes: a card that is
            // already playing keeps playing until it leaves view entirely.
            warmHoverVideo(video);
            if (entry.intersectionRatio < PLAY_RATIO) continue;

            // Wait IN_VIEW_DELAY before revealing and playing, so only a card
            // the customer settles on starts. An intersection that arrives
            // while a countdown is already pending leaves it running.

            if (autoplayBlocked || inViewTimers.has(video)) continue;
            const timer = setTimeout(() => {
              inViewTimers.delete(video);
              // The layer is revealed only once frames are actually moving.
              // Revealing on the play call showed an empty or frozen layer
              // over the photo whenever the file was still loading or the
              // device refused to autoplay; now the photo simply stays.
              video.addEventListener(
                'playing',
                () => {
                  if (!video.paused) card?.classList.add(PLAYING);
                },
                { once: true }
              );
              playHoverVideo(video).then((played) => {
                if (!played) card?.classList.remove(PLAYING);
              });
            }, IN_VIEW_DELAY);
            inViewTimers.set(video, timer);
          } else {
            // Left view before the delay elapsed: cancel the pending start and
            // put the card back to its first frame.
            const timer = inViewTimers.get(video);
            if (timer) {
              clearTimeout(timer);
              inViewTimers.delete(video);
            }
            card?.classList.remove(PLAYING);
            resetHoverVideo(video);
          }
        }
      },
      // Two thresholds: any part of the card starts the download, PLAY_RATIO
      // of it starts the countdown to play.
      { threshold: [0, PLAY_RATIO] }
    );

    for (const video of document.querySelectorAll(`${HOVER_VIDEO}[data-pp-hover-in-view]`)) {
      inViewObserver.observe(video);
    }
  }

  document.addEventListener('shopify:section:load', watchHoverVideos);
  document.addEventListener('shopify:section:load', warmDesktopHoverVideos);
  watchHoverVideos();
  warmDesktopHoverVideos();
})();
